/*                                                                                                                     
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--'

Copyright (c) 2026 Dynamic IOT Data, Inc. All rights reserved.

@author Andrew Craig
*/
import { VisionApplication } from '../../application';
import { Measurement } from '../../components/measurement';
import { PublishTopic } from '../../events';
import { VisionLayout } from '../../layout';
import { AssetDTO } from '../../service/asset';
import { VisionGroupList } from '../components/group_list';
import { AdjustDueHoursWindow } from './adjustduehours_window';
import { AdjustDueOdometerWindow } from './adjustdueodometer_window';
import { PMCompleteWindow } from './complete_window';
import { PMNotesWindow } from './notes_window';
import { PMHistoryWindow } from './pm_history';
import { PMWindow } from './pm_window';
import { PMDTO, PMType, PMTypeDTO, PreventativeMaintenanceService } from './pmservice';
import { PMTypeWindow } from './pmtype_window';
import { PMSelectWindow } from './select_window';

/** Select an Asset Window.
 *
 */
class AssetSelectWindow {
    window: dhtmlXWindowsCell;

    /** Constructor for the Asset Select Window.
     *
     * @param win   The dhtmlXWindows main object.
     * @param parent The tab owner.
     */
    constructor(win: dhtmlXWindows, parent: PreventativeMaintenanceTab) {
        const app = VisionApplication.getInstance();
        const form = [
            {
                name: 'asset',
                type: 'select',
                label: 'Asset',
                value: '',
                offsetLeft: '25',
                required: true,
                inputWidth: 100,
                options: [{ text: 'None', value: 'NONE' }],
            },
            { type: 'button', name: 'select_asset', value: 'Select' },
        ];
        const list = new Array<AssetDTO>();
        const existing = parent.grid.getAllRowIds(',').split(',');

        app.assetService.assetList.forEach((asset) => {
            if (existing.includes(asset.id.id) == false) {
                list.push(asset);
            }
        });

        list.sort((one: AssetDTO, two: AssetDTO) => {
            const a = one.id.name;
            const b = two.id.name;
            return a.localeCompare(b, undefined, {
                numeric: true,
                sensitivity: 'base',
            });
        });
        list.forEach((asset) => {
            form[0].options?.push({ text: asset.id.name, value: asset.id.id });
        });

        this.window = win.createWindow('pm_asset_select', 30, 30, 400, 200);
        this.window.setText('Select Vehicle');
        const formObj = this.window.attachForm(form);
        formObj.attachEvent('onButtonClick', (name) => {
            if (name == 'select_asset') {
                parent.createNewAssetRow(formObj.getItemValue('asset'));
                this.window.close();
            }
        });
    }
}

//=================================================================================================

export enum PM_Type {
    HOURS = 0,
    MILES = 1,
    DATE = 2,
}

/** Main PM Tab
 *
 */
export class PreventativeMaintenanceTab {
    okColour = '#bcfdbc';
    dueColour = '#FFA500';
    overdueColour = '#F08080';

    // UI Elements
    layout!: dhtmlXLayoutObject;
    grid!: dhtmlXGridObject;
    toolbar!: dhtmlXToolbarObject;
    topTenGrid!: dhtmlXGridObject;
    overDueGrid!: dhtmlXGridObject;
    contextMenu: dhtmlXMenuObject;
    infoTabs!: dhtmlXTabBar;
    groupList: VisionGroupList;

    // Services
    app: VisionApplication;
    service: PreventativeMaintenanceService;

    // Windows
    editPMWindow!: PMWindow | undefined;
    completeWindow: PMCompleteWindow | undefined;
    notesWindow: PMNotesWindow | undefined;

    init: boolean;

    lastSort = { col: -1, order: 'asc' };

    // Caches
    pmtypes: Map<string, PMTypeDTO>;
    pmInstances: Map<string, PMDTO>;

    constructor(vision: VisionLayout) {
        this.app = VisionApplication.getInstance();
        this.service = new PreventativeMaintenanceService();
        this.init = false;
        this.completeWindow = undefined;
        this.pmtypes = new Map<string, PMTypeDTO>();
        this.pmInstances = new Map<string, PMDTO>();

        const config: IdhtmlXLayoutConfig = {
            parent: '',
            pattern: '2U',
        };
        this.layout = vision.tabbar.cells('PM').attachLayout(config);
        this.layout.cells('a').setText('Vehicles');
        this.infoTabs = this.layout.cells('b').attachTabbar();
        this.infoTabs.addTab('upcoming', 'Due', 100, 1, true, false);
        this.infoTabs.addTab('overdue', 'Overdue', 100, 2, false, false);

        this.grid = this.layout.cells('a').attachGrid();
        this.grid.setHeader('Name,Odometer <i class="fas fa-tachometer-alt"></i>,Hours<i class="fa-solid fa-hourglass-start"></i>,Days,Date,Status,Done', '', [
            'text-align:left;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
        ]);
        this.grid.setColAlign('left,center,center,center,center,center,center');
        this.grid.setColSorting('str,int,int,str,int,int,na');
        this.grid.setColTypes('tree,ro,ro,coro,ro,ro,img');

        this.grid.setColumnId(0, 'name');
        this.grid.setColumnId(1, 'odometer');
        this.grid.setColumnId(2, 'hours');
        this.grid.setColumnId(3, 'days');
        this.grid.setColumnId(4, 'date');
        this.grid.setColumnId(5, 'status');
        this.grid.setColumnId(6, 'done');
        this.grid.setImagesPath('css/imgs/');
        this.grid.setHighlight(false);
        this.grid.setEditable(false);

        //this.grid.setInitWidthsP('100,*');
        //this.grid.setInitWidths('*,*,*,*,*,*,*');

        this.grid.setCustomSorting((a, b, ord, aid, bid) => {
            const aName = this.grid.getUserData(aid, 'name');
            const bName = this.grid.getUserData(bid, 'name');
            return VisionApplication.nameSort(aName, bName, ord);
        }, 0);

        this.grid.enableAutoWidth(true);
        this.grid.init();
        this.grid.enableHeaderMenu();
        this.grid.enableAutoWidth(true);
        this.grid.setSizes();
        this.loadHeaderState();

        const container = this.grid.entBox;
        container.classList.add('gridbox_pm_skin');

        //this.topTenGrid = this.layout.cells('b').attachGrid();
        this.topTenGrid = this.infoTabs.cells('upcoming').attachGrid();
        this.overDueGrid = this.infoTabs.cells('overdue').attachGrid();

        this.topTenGrid.setHeader('Vehicle, PM, Due In, Due At', '', ['text-align:left;', 'text-align:center;', 'text-align:center;', 'text-align:center']);
        this.topTenGrid.setColAlign('left,center,center,center');
        this.topTenGrid.setColTypes('ro,ro,ro,ro');
        this.topTenGrid.setColumnIds('vehicle,pm,due_in,due_at');
        this.topTenGrid.setImagesPath('css/imgs/');
        this.topTenGrid.setHighlight(false);
        this.topTenGrid.setColSorting('str,str,int,int');
        this.topTenGrid.init();

        this.overDueGrid.setHeader('Vehicle, PM, Due In, Due At', '', ['text-align:left;', 'text-align:center;', 'text-align:center;', 'text-align:center']);
        this.overDueGrid.setColAlign('left,center,center,center');
        this.overDueGrid.setColTypes('ro,ro,ro,ro');
        this.overDueGrid.setColumnIds('vehicle,pm,due_in,due_at');
        this.overDueGrid.setImagesPath('css/imgs/');
        this.overDueGrid.setHighlight(false);
        this.overDueGrid.setColSorting('str,str,int,int');
        this.overDueGrid.init();

        this.toolbar = this.layout.cells('a').attachToolbar();

        //const groupList = new VisionGroupList(this.toolbar, this.grid, 'asset_folder_pm', this.filter);
        this.groupList = new VisionGroupList(this.toolbar, this.grid, 'asset_folder_pm', this.filter);

        this.toolbar.addButton('new', 1, 'Add Vehicle', 'css/imgs/new.gif', '');
        this.toolbar.addButton('new_pm', 1, 'Manage Types', '/icons/gears.png', '');
        //this.layout.cells('b').attachHTMLString("<div id='pm_chart' style='width:100%;height:100%'></div>");

        this.contextMenu = new dhtmlXMenuObject();
        this.contextMenu.setIconsPath('css/imgs/');
        this.contextMenu.renderAsContextMenu();

        this.contextMenu.loadStruct(`<menu id="0">
                        <item text="Remove" id="remove"/>
                        <item text="Add PM" id="add_pm"/>                        
                        <item text="History" id="history" />        
                        </menu>`);
        this.grid.enableContextMenu(this.contextMenu);

        this.layout.cells('a').hideHeader();
        this.setupEventHandlers();

        this.service.getTypes(false).then((data) => {
            data.forEach((item) => {
                this.pmtypes.set(item.id, item);
            });
            //this.loadData();
        });

        this.topTenGrid.attachEvent('onRowDblClicked', (rowId, _colIndex) => {
            const assetId = this.topTenGrid.getUserData(rowId, 'assetId');
            this.grid.selectRowById(assetId, true, true, false);
        });

        this.overDueGrid.attachEvent('onRowDblClicked', (rowId, _colIndex) => {
            const assetId = this.overDueGrid.getUserData(rowId, 'assetId');
            this.grid.selectRowById(assetId, true, true, false);
        });
    }

    /** Called whenever the tab is selected.
     *
     *  Will do a reload of data to make sure it's using up to date information.
     */
    public onSelect(): void {
        this.loadData(true);
    }

    private filter(id: string) {
        const folder = VisionApplication.getInstance().assetFolderService.folderCache?.get(id);

        this.grid?.forEachRow((rowId) => {
            this.grid.setRowHidden(rowId, true);
        });

        this.grid?.forEachRow((rowId) => {
            this.grid.setRowHidden(rowId, true);

            if (folder?.items.some((e) => e.id === rowId)) {
                this.grid?.setRowHidden(rowId, false);
            }

            console.log('RowId ' + rowId);
            if (folder?.items.some((e) => e.id === this.grid.getUserData(rowId, 'parent'))) {
                this.grid?.setRowHidden(rowId, false);
            }
        });
    }

    /** Setup the asset in the various grids.
     *
     * @param asset The asset associated with the PM instance.
     * @param pmInstance The PM instance to set up.
     * @returns void
     */
    private setupAsset(asset: AssetDTO, pmInstance: PMDTO): void {
        if (this.grid.getRowIndex(pmInstance.assetId) == -1) {
            this.grid.addRow(pmInstance.assetId, asset?.id.name + ',,,,,,,,+', '', '', '', false);
            this.grid.setUserData(pmInstance.assetId, 'type', 'asset');
            this.grid.setUserData(pmInstance.assetId, 'name', asset?.id.name);
            this.grid.setUserData(pmInstance.assetId, 'hoursOffset', asset?.hoursOffset);
            this.grid.setUserData(pmInstance.assetId, 'odoOffsetKm', asset?.odoOffset);
        }

        const rowData = [];

        rowData.push(pmInstance.name);
        const pmtype = this.pmtypes.get(pmInstance.pmtypeid);
        if (pmtype == undefined) {
            return;
        }

        this.processOdometer(asset, pmInstance, pmtype, rowData);
        this.processHours(asset, pmInstance, pmtype, rowData);
        this.processDays(pmInstance, pmtype, rowData);
        this.processDate(pmInstance, pmtype, rowData);

        rowData.push('OK');
        rowData.push('https://www.dynamiciotdata.com/icons/wrench.png');

        this.grid.addRowNoIcons(pmInstance.id, rowData.join(','), '', pmInstance.assetId, '', false);
        this.grid.setUserData(pmInstance.id, 'type', 'pm');
        this.grid.setUserData(pmInstance.id, 'parent', pmInstance.assetId);

        this.updateStatus(asset, pmInstance, pmtype);
        this.pmInstances.set(pmInstance.id, pmInstance);

        return;
    }

    public loadData(refresh = false): void {
        console.log('Loading Data Called');
        this.topTenGrid.clearAll(false);
        this.overDueGrid.clearAll(false);

        if (refresh == true) {
            this.grid.clearAll(false);
        }

        const cnt = 0;
        this.service.loadPM(refresh).then(async (data) => {
            if (data.parsedBody) {
                await Promise.all(
                    data.parsedBody.map(async (item) => {
                        const assetMap = await this.app.assetService.getAssetMap();
                        const asset = assetMap.get(item.assetId);

                        if (asset) {
                            this.setupAsset(asset, item);
                        }
                    })
                );

                this.grid.expandAll();
                this.grid.adjustColumnSize(this.grid.getColIndexById('name'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('odometer'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('hours'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('days'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('date'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('status'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('done'));
                this.grid.sortRows(0, 'str', 'asc');

                this.topTenGrid.sortRows(0, 'str', 'asc');
                this.overDueGrid.sortRows(0, 'str', 'asc');
            }
        });
    }

    /*
                this.grid.forEachRow((rowId) => {
                    this.grid.openItem(rowId);
                });
                this.grid.adjustColumnSize(this.grid.getColIndexById('odometer'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('hours'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('days'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('date'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('status'));
                this.grid.adjustColumnSize(this.grid.getColIndexById('done'));
*/

    /*
    addOverDue(idx: string) {
        //this.topTenGrid.setHeader('Vehicle,PM,Type, Due At, Due In', '', ['text-align:left;', 'text-align:center;', 'text-align:center;', 'text-align:center', 'text-align:center;']);

        const due_in = parseFloat(this.grid.cellById(idx, 5).getValue());
        if (due_in < 0) {
            const assetName = this.grid.getUserData(idx, 'name');
            const pmName = this.grid.cellById(idx, 0).getValue();
            const odo = this.grid.cellById(idx, 1).getValue();
            const hours = this.grid.cellById(idx, 2).getValue();
            const type = this.grid.cellById(idx, 3).getValue();
            const dueAt = this.grid.cellById(idx, 4).getValue();

            const overDue = Math.abs(Number(odo) - Number(dueAt));
            //Vehicle,PM,Type,Odometer,Hours,Due
            if (this.overDueGrid.getRowIndex(idx) == -1) {
                this.overDueGrid.addRow(idx, [assetName, pmName, type, odo, hours, overDue], 0, '', '', false);
            }
        }
    }
    */

    createNewAssetRow(id: string): void {
        const asset = this.app.assetService.assetList.get(id);

        if (asset) {
            this.grid.addRow(asset.id.id, asset.id.name + ',,,,,,,+', '', '', '', false);
            this.grid.setUserData(asset.id.id, 'type', 'asset');
        }
    }

    private typeToNumber(type: string) {
        switch (type) {
            case 'NONE':
                return -1;
            case 'HOURS':
                return 0;
            case 'MILES':
                return 1;
            case 'DAYS':
                return 4;
            default:
                return 0;
        }
    }

    private setupEventHandlers(): void {
        this.grid.attachEvent('onValidationError', function (id, index, value, rule) {
            alert('VALIDATION ERROR');
            console.log(id);
            console.log(index);
            console.log(value);
            console.log(rule);

            return true;
        });

        //-----------------------------------------------------------------------------------------
        // EVENT:  User changes the header checkbox state. Save the state to local storage so it can be restored on next load.U
        this.grid.getHeaderMenu(0).attachEvent('onCheckboxClick', (_id: string | number, _state: boolean, _zoneId: string | number, _cas: unknown) => {
            this.saveHeaderState(_id, _state);
            return true;
        });
        //-----------------------------------------------------------------------------------------

        //-----------------------------------------------------------------------------------------
        // EVENT: User clicks on a toolbar button.
        this.toolbar.attachEvent('onClick', (itemId) => {
            if (itemId == 'new') {
                //const win = new AssetSelectWindow(this.layout.dhxWins, this);
                new AssetSelectWindow(this.layout.dhxWins, this);
            }
            if (itemId == 'new_pm') {
                //const pmwin = new PMTypeWindow(this.app.windowManager, this);
                new PMTypeWindow(this.app.windowManager, this);
            }
        });
        //-----------------------------------------------------------------------------------------

        //-----------------------------------------------------------------------------------------
        // EVENT: User clicks on a context menu item.
        this.contextMenu.attachEvent('onClick', (menuItemId, _type) => {
            const idx = this.grid.contextID.slice(0, -2);
            const datatype = this.grid.getUserData(idx, 'type');

            if (datatype == 'asset') {
                if (menuItemId == 'remove') {
                    this.removeAsset(idx);
                }

                if (menuItemId == 'add_pm') {
                    this.addNewPM(idx);
                }

                if (menuItemId == 'history') {
                    const hiswin = new PMHistoryWindow(this.app.windowManager, this);
                    hiswin.setAsset(idx);
                }
            }
            if (datatype == 'pm') {
                this.removePM(idx);
            }
        });
        //-----------------------------------------------------------------------------------------

        //-----------------------------------------------------------------------------------------
        // EVENT: User right clicks on a row in the grid. Show or hide context menu items based on the type of row.
        this.grid.attachEvent('onBeforeContextMenu', (idx, _ind, _obj) => {
            this.contextMenu?.hideItem('add_pm');
            this.contextMenu?.hideItem('history');

            const datatype = this.grid.getUserData(idx, 'type');
            if (datatype == 'asset') {
                this.contextMenu?.showItem('add_pm');
                this.contextMenu?.showItem('history');
            }
            return true;
        });
        //-----------------------------------------------------------------------------------------

        //-----------------------------------------------------------------------------------------
        // EVENT:  Used selects a PM row.  If clicked on the spanner icons opens the notes window.
        this.grid.attachEvent('onRowSelect', (rowId, index) => {
            const rowType = this.grid.getUserData(rowId, 'type');
            if (rowType == 'pm' && index == 6) {
                this.getAction(rowId);
                return true;
            } else {
                return false;
            }
        });

        this.grid.attachEvent('onRowDblClicked', (rowId, index) => {
            const rowType = this.grid.getUserData(rowId, 'type');
            if (rowType == 'pm') {
                // Column 1 is the odometer values
                if (index == 1) {
                    this.showAdjustDueOdometerWindow(rowId);
                    return true;
                }

                // Column 2 is the hours values.
                if (index == 2) {
                    this.showAdjustDueHoursWindow(rowId);
                    return true;
                }

                return false;
            } else {
                return false;
            }
        });
        //-----------------------------------------------------------------------------------------
    }

    public showAdjustDueOdometerWindow(pmInstanceId: string) {
        const assetId = this.grid.getUserData(pmInstanceId, 'parent');
        const asset = VisionApplication.getInstance().assetService.assetList.get(assetId);
        const pmInstance = this.pmInstances.get(pmInstanceId);
        const pmName = this.pmtypes.get(pmInstance ? pmInstance.pmtypeid : '');

        const vehicleDueOdometerKm = pmInstance ? pmInstance.vehicleOdoDueAtKm : 0;

        const window = new AdjustDueOdometerWindow(this.app.windowManager.win, this);
        window.setVehicleDueOdometerKm(vehicleDueOdometerKm);
        window.setPmInstanceId(pmInstanceId);
        window.setAssetId(assetId);
        window.setAssetName(asset ? asset.id.name : '');
        window.setPmName(pmName ? pmName.name : '');
        window.show();
    }

    public showAdjustDueHoursWindow(pmInstanceId: string) {
        const assetId = this.grid.getUserData(pmInstanceId, 'parent');
        const asset = VisionApplication.getInstance().assetService.assetList.get(assetId);
        const pmInstance = this.pmInstances.get(pmInstanceId);
        const pmName = this.pmtypes.get(pmInstance ? pmInstance.pmtypeid : '');

        const vehicleDueHours = pmInstance ? pmInstance.vehicleHoursDueAt : 0;

        const window = new AdjustDueHoursWindow(this.app.windowManager.win, this);
        window.setVehicleDueHours(vehicleDueHours);
        window.setPmInstanceId(pmInstanceId);
        window.setAssetId(assetId);
        window.setAssetName(asset ? asset.id.name : '');
        window.setPmName(pmName ? pmName.name : '');
        window.show();
    }

    public showCompleteWindow(pmInstanceId: string) {
        const assetId = this.grid.getUserData(pmInstanceId, 'parent');
        //const asset = VisionApplication.getInstance().assetService.assetList.get(assetId);

        // Make sure to get the latest asset information
        VisionApplication.getInstance()
            .assetService.getAssetMap()
            .then((assetMap) => {
                const asset = assetMap.get(assetId);

                const pmInstance = this.pmInstances.get(pmInstanceId);
                const pmType = pmInstance ? this.pmtypes.get(pmInstance.pmtypeid) : undefined;

                if (this.completeWindow == undefined) {
                    this.completeWindow = new PMCompleteWindow(this.app.windowManager, this);
                    this.completeWindow.window.show();
                    this.completeWindow.loadPM(pmInstance, pmType, asset);
                } else {
                    this.completeWindow.window.show();
                    this.completeWindow.loadPM(pmInstance, pmType, asset);
                }
            });
    }

    /** This used to ask if the user wanted to add notes but now will just open the notes window directly.
     * The notes window has a button to complete the PM which will open the complete window if the user wants to complete the PM after adding notes.
     *
     * @param pmInstanceId The ID of the PM instance that the user wants to add notes for.
     *
     */
    private getAction(pmInstanceId: string): void {
        const assetId = this.grid.getUserData(pmInstanceId, 'parent');
        const asset = VisionApplication.getInstance().assetService.assetList.get(assetId);
        const pmInstance = this.pmInstances.get(pmInstanceId);
        const pmType = pmInstance ? this.pmtypes.get(pmInstance.pmtypeid) : undefined;

        if (this.notesWindow == undefined) {
            this.notesWindow = new PMNotesWindow(this.app.windowManager, this);
            this.notesWindow.loadPM(pmInstance, pmType, asset);
            this.notesWindow.window.show();
        } else {
            this.notesWindow.show();
            this.notesWindow.loadPM(pmInstance, pmType, asset);
        }
        /*
        dhtmlx.confirm({
            type: 'confirm',
            title: 'PM Action',
            text: '',
            ok: 'PM Completed',
            cancel: 'Add Notes',
            callback: (result: boolean) => {
                console.log('PM RESULT: ' + result);
                const assetId = this.grid.getUserData(pmInstanceId, 'parent');
                const asset = VisionApplication.getInstance().assetService.assetList.get(assetId);
                const pmInstance = this.pmInstances.get(pmInstanceId);
                const pmType = pmInstance ? this.pmtypes.get(pmInstance.pmtypeid) : undefined;

                if (result == true) {
                    if (this.completeWindow == undefined) {
                        this.completeWindow = new PMCompleteWindow(this.app.windowManager, this);
                        this.completeWindow.window.show();
                        this.completeWindow.loadPM(pmInstance, pmType, asset);
                    } else {
                        this.completeWindow.window.show();
                        this.completeWindow.loadPM(pmInstance, pmType, asset);
                    }
                }
                if (result == false) {
                    if (this.notesWindow == undefined) {
                        this.notesWindow = new PMNotesWindow(this.app.windowManager, this);
                        this.notesWindow.loadPM(pmInstance, pmType, asset);
                        this.notesWindow.window.show();
                    } else {
                        this.notesWindow.show();
                        this.notesWindow.loadPM(pmInstance, pmType, asset);
                    }
                }
            },
        });
        */
    }

    private openPMWindow(id: string) {
        if (this.editPMWindow == undefined) {
            this.editPMWindow = new PMWindow(this.app.windowManager, this);
        }
        this.editPMWindow.setPM(id);
        this.editPMWindow.show();
    }

    private addNewPM(assetid: string) {
        //const win = new PMSelectWindow(this.layout.dhxWins, this, assetid);
        new PMSelectWindow(this.layout.dhxWins, this, assetid);
    }

    /** Remove an asset from the system.
     * Will also remove any PM's associated with that asset.
     *
     * @param assetid the id of the asset to remove.
     */
    private removeAsset(assetid: string) {
        dhtmlx.confirm({
            type: 'confirm-warning',
            title: 'Confirm Delete',
            text: 'Delete Asset and its PMs?',
            callback: (result: boolean) => {
                if (result == true) {
                    this.service.removeAsset(assetid).then((_data) => {
                        this.grid.clearAll(false);
                        this.loadData();
                    });
                }
            },
        });
    }

    private removePM(pmid: string) {
        dhtmlx.confirm({
            type: 'confirm-warning',
            title: 'Confirm Delete',
            text: 'Delete PM?',
            callback: (result: boolean) => {
                if (result == true) {
                    this.service.removePM(pmid).then((_data) => {
                        this.grid.clearAll(false);
                        this.loadData(true);
                    });
                }
            },
        });
    }

    public createPM(pid: string, assetid: string, odoKm: number, hours: number, days?: number, date?: number, currentOdometerKm?: number, currentHours?: number): void {
        console.log('Creating PM with PID ' + pid + ' assetid ' + assetid + ' odoKm ' + odoKm + ' hours ' + hours + ' days ' + days + ' date ' + date);

        this.service.createNewPMFromType(pid, assetid, odoKm, hours, days, date, currentOdometerKm, currentHours).then((_data) => {
            this.app.assetService.setAssetMapDirty();
            this.grid.clearAll(false);
            this.loadData(true);
            VisionApplication.getInstance().pubsub.publish(PublishTopic.PM_UPDATED);
        });
    }

    private processOdometer(asset: AssetDTO, pm: PMDTO, pmtype: PMTypeDTO, rowData: string[]) {
        const odoOffset = asset.odoOffset == undefined ? 0 : asset.odoOffset;
        if (pm.odometerDueAt != undefined) {
            //const remaining = Math.abs(pm.odometerDueAt - pm.currentOdometer);
            const currentOdometer = pm.currentOdometer + odoOffset;
            const remaining = Math.abs(pm.vehicleOdoDueAtKm - (pm.currentOdometer + odoOffset));

            rowData.push(Measurement.km2miles(currentOdometer) + ' / ' + Measurement.km2miles(pm.vehicleOdoDueAtKm).toFixed(0) + ' (' + Measurement.km2miles(remaining).toFixed(0) + ' miles)');
        } else {
            rowData.push('');
        }
    }

    private processHours(asset: AssetDTO, pm: PMDTO, pmtype: PMTypeDTO, rowData: string[]) {
        const hoursOffset = asset.hoursOffset == undefined ? 0 : asset.hoursOffset;
        if (pm.vehicleHoursDueAt != undefined) {
            pm.vehicleHoursDueAt = Number(pm.vehicleHoursDueAt);
            const currentHours = pm.currentHours + hoursOffset;
            const remaining = Math.abs(pm.vehicleHoursDueAt - currentHours);

            const displayCurrentHours = currentHours.toFixed(0);
            const displayVehicleHoursDueAt = Number(pm.vehicleHoursDueAt).toFixed(0);
            const displayRemaining = remaining.toFixed(0);

            rowData.push(displayCurrentHours + ' / ' + displayVehicleHoursDueAt + ' (' + displayRemaining + ' hours)');
        } else {
            rowData.push('');
        }
    }

    private processDays(pm: PMDTO, pmtype: PMTypeDTO, rowData: string[]) {
        if (pm.daysDueAt != undefined) {
            const daysBefore = Math.ceil((pm.daysDueAt - Date.now()) / (1000 * 60 * 60 * 24));
            rowData.push(daysBefore + ' days  ' + new Date(pm.daysDueAt).toLocaleDateString());
        } else {
            rowData.push('');
        }
    }

    private processDate(pm: PMDTO, pmtype: PMTypeDTO, rowData: string[]) {
        if (pm.dateDueAt != undefined) {
            const daysBefore = Math.ceil((pm.dateDueAt - Date.now()) / (1000 * 60 * 60 * 24));
            rowData.push(daysBefore + ' days ' + new Date(pm.dateDueAt).toLocaleDateString());
        } else {
            rowData.push('');
        }
    }

    private setOdometerStatus(pm: PMDTO, pmtype: PMTypeDTO): number {
        if (pm.odometerDueAt == undefined) {
            return 0;
        }
        const rowId = pm.id;
        let status = 0;
        const odoOffset = this.grid.getUserData(pm.assetId, 'odoOffsetKm') == undefined ? 0 : this.grid.getUserData(pm.assetId, 'odoOffsetKm');

        //const remaining = pm.odometerDueAt - pm.currentOdometer;
        const remaining = pm.vehicleOdoDueAtKm - (pm.currentOdometer + odoOffset);

        let overdue = '';
        if (remaining < 0) {
            overdue = 'overdue';
        }
        const remainingValue = Measurement.km2miles(Math.abs(remaining)).toFixed(0);

        if (remaining < (pmtype?.odometerAlertKm == undefined ? 0 : pmtype?.odometerAlertKm)) {
            this.grid.cells(rowId, 1).setBgColor('#FFA5FF');
            status |= 1;
            const newRowId = crypto.randomUUID();
            this.topTenGrid.addRow(
                newRowId,
                [
                    this.grid.getUserData(pm.assetId, 'name'),
                    pm.name + ' <i class="fas fa-tachometer-alt"></i>',
                    remainingValue + ' (miles ' + overdue + ')',
                    Measurement.km2miles(pm.vehicleOdoDueAtKm).toFixed(0) + ' (miles)',
                ],
                0,
                '',
                '',
                false
            );
            this.topTenGrid.setUserData(newRowId, 'pmInstanceId', pm.id);
            this.topTenGrid.setUserData(newRowId, 'assetId', pm.assetId);
        }

        if (pm.currentOdometer + odoOffset > (pm.vehicleOdoDueAtKm == undefined ? 0 : pm.vehicleOdoDueAtKm)) {
            this.grid.cells(rowId, 1).setBgColor('#FFA5FF');
            status |= 2;
        }

        if (pm.currentOdometer > pm.odometerDueAt) {
            const newRowId = crypto.randomUUID();
            this.overDueGrid.addRow(
                newRowId,
                [
                    this.grid.getUserData(pm.assetId, 'name'),
                    pm.name + ' (odometer)',
                    remainingValue + ' (miles ' + overdue + ')',
                    Measurement.km2miles(pm.odometerDueAt + odoOffset).toFixed(0) + ' (miles)',
                ],
                0,
                '',
                '',
                true
            );
            this.overDueGrid.setUserData(newRowId, 'pmInstanceId', pm.id);
            this.overDueGrid.setUserData(newRowId, 'assetId', pm.assetId);
        }
        return status;
    }

    private setHoursStatus(asset: AssetDTO, pm: PMDTO, pmtype: PMTypeDTO): number {
        if (pm.hoursDueAt == undefined) {
            return 0;
        }
        const rowId = pm.id;
        let status = 0;
        const hoursOffset = this.grid.getUserData(pm.assetId, 'hoursOffset') == undefined ? 0 : this.grid.getUserData(pm.assetId, 'hoursOffset');

        const remaining = pm.vehicleHoursDueAt - (pm.currentHours + hoursOffset);
        let overdue = '';
        if (remaining < 0) {
            overdue = 'overdue';
        }
        const remainingValue = Math.abs(remaining).toFixed(0);

        if (remaining < (pmtype?.hoursAlert == undefined ? 0 : pmtype?.hoursAlert)) {
            this.grid.cells(rowId, 2).setBgColor('#FFA5FF');
            status |= 1;
            const newRowId = crypto.randomUUID();
            this.topTenGrid.addRow(
                newRowId,
                [asset.id.name, pm.name + ' <i class="fa-solid fa-hourglass-start"></i>', remainingValue + ' (hours ' + overdue + ')', pm.vehicleHoursDueAt.toFixed(0) + ' (hours)'],
                0,
                '',
                '',
                false
            );
            this.topTenGrid.setUserData(newRowId, 'pmInstanceId', pm.id);
            this.topTenGrid.setUserData(newRowId, 'assetId', pm.assetId);
        }
        if (pm.currentHours + hoursOffset > (pm.vehicleHoursDueAt == undefined ? 0 : pm.vehicleHoursDueAt)) {
            this.grid.cells(rowId, 2).setBgColor('#FFA5FF');
            status |= 2;
        }

        if (pm.currentHours + hoursOffset > pm.vehicleHoursDueAt) {
            const newRowId = crypto.randomUUID();
            this.overDueGrid.addRow(newRowId, [asset.id.name, pm.name + ' (hours)', remainingValue + ' (hours ' + overdue + ')', pm.vehicleHoursDueAt.toFixed(0) + ' (hours)'], 0, '', '', true);
            this.overDueGrid.setUserData(newRowId, 'pmInstanceId', pm.id);
            this.overDueGrid.setUserData(newRowId, 'assetId', pm.assetId);
        }
        return status;
    }

    private setDaysStatus(asset: AssetDTO, pm: PMDTO, pmtype: PMTypeDTO): number {
        if (pm.daysDueAt == undefined) {
            return 0;
        }
        const rowId = pm.id;
        let status = 0;
        const daysBefore = Math.ceil((pm.daysDueAt == undefined ? 0 : pm.daysDueAt - Date.now()) / (1000 * 60 * 60 * 24));
        if (daysBefore < (pmtype?.daysAlert == undefined ? 0 : pmtype?.daysAlert) && daysBefore > 0) {
            this.grid.cells(rowId, 3).setBgColor('#FFA5FF');
            status |= 1;
            const newRowId = crypto.randomUUID();
            this.topTenGrid.addRow(newRowId, [asset.id.name, pm.name + ' (days)', daysBefore + ' (days)', new Date(pm.daysDueAt).toLocaleDateString()], 0, '', '', false);
            this.topTenGrid.setUserData(newRowId, 'pmInstanceId', pm.id);
            this.topTenGrid.setUserData(newRowId, 'assetId', pm.assetId);
        } else if (daysBefore < 0) {
            this.grid.cells(rowId, 3).setBgColor('#FFA5FF');
            status |= 2;
        }
        return status;
    }

    private setDateStatus(asset: AssetDTO, pm: PMDTO, pmtype: PMTypeDTO): number {
        if (pm.dateDueAt == undefined) {
            return 0;
        }
        const rowId = pm.id;
        let status = 0;
        const daysBefore = Math.ceil((pm.dateDueAt == undefined ? 0 : pm.dateDueAt - Date.now()) / (1000 * 60 * 60 * 24));
        if (daysBefore < (pmtype?.dateAlert == undefined ? 0 : pmtype?.dateAlert)) {
            this.grid.cells(rowId, 4).setBgColor('#FFA5FF');
            status |= 1;
            const newRowId = crypto.randomUUID();
            this.topTenGrid.addRow(newRowId, [asset.id.name, pm.name + ' (date)', daysBefore + ' (days)', new Date(pm.dateDueAt).toLocaleDateString()], 0, '', '', false);
            this.topTenGrid.setUserData(newRowId, 'pmInstanceId', pm.id);
            this.topTenGrid.setUserData(newRowId, 'assetId', pm.assetId);
        } else if (daysBefore < 0) {
            this.grid.cells(rowId, 4).setBgColor('#FFA5FF');
            status |= 2;
        }
        return status;
    }

    private updateStatus(asset: AssetDTO, pm: PMDTO, pmtype: PMTypeDTO): void {
        const rowId = pm.id;
        let status = 0;
        status |= this.setOdometerStatus(pm, pmtype);
        status |= this.setHoursStatus(asset, pm, pmtype);
        status |= this.setDaysStatus(asset, pm, pmtype);
        status |= this.setDateStatus(asset, pm, pmtype);

        if (status == 2 || status == 3) {
            this.grid.setRowColor(rowId, this.overdueColour);
            this.grid.cells(rowId, 5).setValue('OVERDUE');
        } else if (status == 1) {
            this.grid.setRowColor(rowId, this.dueColour);
            this.grid.cells(rowId, 5).setValue('DUE');
        } else {
            this.grid.setRowColor(rowId, this.okColour);
            this.grid.cells(rowId, 5).setValue('OK');
        }
    }

    /** Save the state of the header menu checkboxes to local storage so that they can be restored on reload.
     *
     */
    private saveHeaderState(_id?: string | number, boxState?: boolean) {
        const stateString = localStorage.getItem('pm_headerState');
        let state = {
            odometer: true,
            hours: true,
            days: true,
            date: true,
            status: true,
            done: true,
        };
        if (stateString) {
            state = JSON.parse(stateString);
        }
        const columnId = this.grid.getColumnId(Number(_id));
        switch (columnId) {
            case 'odometer':
                state.odometer = !state.odometer;
                break;
            case 'hours':
                state.hours = !state.hours;
                break;
            case 'days':
                state.days = !state.days;
                break;
            case 'date':
                state.date = !state.date;
                break;
            case 'status':
                state.status = !state.status;
                break;
            case 'done':
                state.done = !state.done;
                break;
        }

        localStorage.setItem('pm_headerState', JSON.stringify(state));
    }

    /** Load the state of the header menu checkboxes from local storage and apply itbb to the grid.
     *
     */
    private loadHeaderState() {
        const stateString = localStorage.getItem('pm_headerState');
        if (stateString) {
            const state = JSON.parse(stateString);
            this.grid.setColumnHidden(this.grid.getColIndexById('odometer'), !state.odometer);
            this.grid.setColumnHidden(this.grid.getColIndexById('hours'), !state.hours);
            this.grid.setColumnHidden(this.grid.getColIndexById('days'), !state.days);
            this.grid.setColumnHidden(this.grid.getColIndexById('date'), !state.date);
            this.grid.setColumnHidden(this.grid.getColIndexById('status'), !state.status);
            this.grid.setColumnHidden(this.grid.getColIndexById('done'), !state.done);
        }
    }
}
